import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { log } from "@/lib/log";
import { toast } from "sonner";
import { CalendarClock, Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function TimeOff() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [balances, setBalances] = useState(null);
  const [form, setForm] = useState({
    type: "vacation",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date().toISOString().slice(0, 10),
    hours: 8,
    note: "",
  });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const list = await api.get("/time-off");
    setRows(list.data);
    if (user?.employee_id) {
      try {
        const { data } = await api.get(`/employees/${user.employee_id}/balances`);
        setBalances(data);
      } catch (err) {
        // Balances are decorative; log and continue with the request form.
        log.warn("Failed to load benefit balances:", err?.message || err);
      }
    }
  }, [user]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/time-off", { ...form, hours: Number(form.hours) });
      toast.success("Request submitted");
      setForm({ ...form, hours: 8, note: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="p-10">
      <div className="overline mb-2 flex items-center gap-2"><CalendarClock className="w-4 h-4" /> § TIME OFF</div>
      <h1 className="font-heading font-black text-5xl tracking-tight mb-8">Request Time Off</h1>

      <div className="grid grid-cols-3 gap-4 mb-8">
        <Card label="Vacation Balance" val={balances ? `${balances.vacation_balance_hours.toFixed(2)}h` : "—"} color="text-good" testid="vacation-balance" />
        <Card label="Sick Balance" val={balances ? `${balances.sick_balance_hours.toFixed(2)}h` : "—"} color="text-warn" testid="sick-balance" />
        <Card label="Accrual / Period" val={balances ? `V ${balances.vacation_accrual_per_period}h  ·  S ${balances.sick_accrual_per_period}h` : "—"} />
      </div>

      <form onSubmit={submit} className="hard-card p-6 max-w-2xl mb-8">
        <div className="overline mb-4">§ NEW REQUEST</div>
        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <div className="overline mb-2">Type</div>
            <select data-testid="time-off-type" value={form.type} onChange={upd("type")} className="input">
              <option value="vacation">Vacation</option>
              <option value="sick">Sick</option>
            </select>
          </label>
          <label className="block">
            <div className="overline mb-2">Hours</div>
            <input data-testid="time-off-hours" type="number" min="0.25" step="0.25" required value={form.hours} onChange={upd("hours")} className="input" />
          </label>
          <label className="block">
            <div className="overline mb-2">Start Date</div>
            <input data-testid="time-off-start" type="date" required value={form.start_date} onChange={upd("start_date")} className="input" />
          </label>
          <label className="block">
            <div className="overline mb-2">End Date</div>
            <input data-testid="time-off-end" type="date" required value={form.end_date} onChange={upd("end_date")} className="input" />
          </label>
          <label className="col-span-2 block">
            <div className="overline mb-2">Note (optional)</div>
            <textarea data-testid="time-off-note" rows={2} value={form.note} onChange={upd("note")} className="input" />
          </label>
        </div>
        <button data-testid="time-off-submit" type="submit" disabled={busy} className="mt-4 hard-btn px-5 py-2.5 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2">
          <Send className="w-3.5 h-3.5" /> {busy ? "Submitting…" : "Submit Request"}
        </button>
      </form>

      <div className="mb-3"><div className="overline mb-1">§ MY REQUESTS</div><h2 className="font-heading font-black text-3xl tracking-tight">History</h2></div>
      <div className="hard-card overflow-x-auto">
        <table className="w-full font-mono text-sm" data-testid="time-off-table">
          <thead className="bg-ink text-white">
            <tr>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Requested</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Type</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Dates</th>
              <th className="text-right px-3 py-3 uppercase text-xs tracking-widest">Hours</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Status</th>
              <th className="text-left px-3 py-3 uppercase text-xs tracking-widest">Note</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">No requests yet.</td></tr>}
            {rows.map((r, i) => <TimeOffRow key={r.id} r={r} idx={i} />)}
          </tbody>
        </table>
      </div>
      <style>{`.input{width:100%;padding:0.65rem 0.75rem;border:2px solid #0A0A0A;font-family:"IBM Plex Mono",monospace;font-size:0.85rem;background:#fff}`}</style>
    </div>
  );
}

function statusPillClass(status) {
  if (status === "approved") return "bg-good text-white";
  if (status === "rejected") return "bg-bad text-white";
  return "bg-warn text-ink";
}

function TimeOffRow({ r, idx }) {
  return (
    <tr className={idx % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"} data-testid={`time-off-row-${r.id}`}>
      <td className="px-3 py-3 border-t border-ink/20">{new Date(r.created_at).toLocaleDateString()}</td>
      <td className="px-3 py-3 border-t border-ink/20 uppercase">{r.type}</td>
      <td className="px-3 py-3 border-t border-ink/20">{r.start_date} → {r.end_date}</td>
      <td className="px-3 py-3 border-t border-ink/20 text-right font-black">{r.hours.toFixed(2)}h</td>
      <td className="px-3 py-3 border-t border-ink/20">
        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${statusPillClass(r.status)}`}>{r.status}</span>
      </td>
      <td className="px-3 py-3 border-t border-ink/20 italic text-muted-foreground max-w-xs">{r.admin_note || r.note || "—"}</td>
    </tr>
  );
}

function Card({ label, val, color = "", testid }) {
  return (
    <div className="hard-card p-4">
      <div className="overline mb-1">{label}</div>
      <div className={`font-mono font-black text-2xl ${color}`} data-testid={testid}>{val}</div>
    </div>
  );
}
